'use client';

import type { ChipItem } from '@eliadmin/core/src/components/svetech/ui/chip';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import {
  NoResults,
  ProductResults,
  ResultsSkeleton,
} from '@eliadmin/core/src/components/svetech/listing/result-list';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

/** Product results of `/vyhladavanie` — the core advanced search query and analytics. */
export function AdvancedSearchResults({ categories }: { categories: ChipItem[] }) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const searchParams = useSearchParams();

  const query = buildProductsQuery(parseProductSearchParams(searchParams));
  query.results = 12;
  const requestUri = buildProductsRequest(query).uri;
  const resetRequestUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const { data: products, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, requestUri, me?.authToken ?? null],
    queryFn: () => fetchProducts({ locale, query, authToken: me?.authToken }),
    enabled: isInitialized,
  });

  return (
    <div aria-live="polite" aria-busy={isLoading || !isInitialized}>
      {isLoading || !isInitialized ? (
        <ResultsSkeleton />
      ) : products?.data.length ? (
        <ProductResults
          products={products}
          listId="search_advanced"
          resetWhen={[resetRequestUri]}
        />
      ) : (
        <NoResults categories={categories} />
      )}
    </div>
  );
}
